import React, { useState, useEffect } from 'react';
import { FaRupeeSign, FaArrowRight, FaQrcode } from "react-icons/fa";
import { motion } from "framer-motion";

const UPIPay = () => {
  const [amount, setAmount] = useState('');
  const [qrData, setQrData] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const UpiID = "teleroidgroup@axl";
  const RecipientName = "Abhishek";

  // Popular UPI apps logos
  const upiApps = [
    { name: "Google Pay", logo: "https://i.ibb.co/Wp3rvsLK/Google-Pay-Logo.png" },
    { name: "PhonePe", logo: "https://i.ibb.co/MyhvH3XC/phonepe.webp" },
    { name: "BHaratPe", logo: "https://i.ibb.co/nNcQCXnw/bharatpe.png" },
    { name: "BHIM", logo: "https://i.ibb.co/gbNP1F0G/bhim.png" },
    { name: "Amazon Pay", logo: "https://i.ibb.co/KxWgjNr6/amazon.png" },
  ];

  useEffect(() => {
    if (amount) {
      setIsGenerating(true);
      const sanitizedAmount = parseFloat(amount);
      
      // Simulate generation delay for better UX
      const timer = setTimeout(() => {
        if (!isNaN(sanitizedAmount) && sanitizedAmount > 0) {
          const upiURL = `upi://pay?pa=${UpiID}&am=${sanitizedAmount}&pn=${RecipientName}&cu=INR`;
          setQrData(upiURL);
        } else {
          setQrData('');
        }
        setIsGenerating(false);
      }, 800);

      return () => clearTimeout(timer);
    } else {
      setQrData('');
    }
  }, [amount]);

  const handlePayThroughUPI = () => {
    if (qrData) {
      window.location.href = qrData;
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-gray-900 to-black p-4">
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-gradient-to-br from-gray-800 to-gray-900 p-8 rounded-3xl shadow-2xl w-full max-w-md text-white border border-gray-700"
      >
        <div className="text-center mb-6">
          <motion.h1 
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="text-4xl font-bold mb-2 bg-gradient-to-r from-indigo-400 to-purple-500 bg-clip-text text-transparent"
          >
            Instant UPI Pay
          </motion.h1>
          <p className="text-gray-400">Send money directly to {RecipientName}</p>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-400 mb-2">Enter Amount (₹)</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaRupeeSign className="text-gray-500" />
            </div>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="block w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-xl shadow-inner text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition duration-200"
              placeholder="0.00"
              min="1"
              step="1"
            />
          </div>
        </div>

        {isGenerating ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-pulse flex flex-col items-center">
              <div className="w-48 h-48 bg-gray-700 rounded-xl mb-4"></div>
              <p className="text-gray-400">Generating QR Code...</p>
            </div>
          </div>
        ) : qrData ? (
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="flex flex-col items-center"
          >
            <div className="bg-white p-5 rounded-2xl shadow-lg mb-4 relative">
              <div className="absolute -top-3 -right-3 bg-indigo-500 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-md">
                <FaQrcode size={18} />
              </div>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(qrData)}&bgcolor=ffffff&color=000000&qzone=1`}
                alt="UPI QR Code"
                className="w-52 h-52 rounded-lg"
              />
              <div className="mt-3 text-center">
                <p className="text-gray-800 font-medium">Scan to Pay</p>
                <p className="text-lg font-bold text-gray-900">₹{amount}</p>
              </div>
            </div>

            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={handlePayThroughUPI}
              className="mt-4 w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white py-3 px-6 rounded-xl hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 transition duration-300 flex items-center justify-center"
            >
              Open in UPI App
              <FaArrowRight className="ml-2" />
            </motion.button>

            <div className="mt-6 w-full">
              <p className="text-center text-gray-400 mb-3">Supported Apps</p>
              <div className="flex justify-center space-x-4">
                {upiApps.map((app, index) => (
                  <motion.div 
                    key={index}
                    whileHover={{ y: -3 }}
                    className="tooltip" 
                    data-tip={app.name}
                  >
                    <img 
                      src={app.logo} 
                      alt={app.name} 
                      className="h-10 object-contain opacity-80 hover:opacity-100 transition" 
                    />
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        ) : (
          <div className="flex flex-col items-center justify-center h-64 bg-gray-800 rounded-xl border-2 border-dashed border-gray-700">
            <div className="text-center p-6">
              <div className="mx-auto w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center mb-4">
                <FaRupeeSign size={24} className="text-gray-500" />
              </div>
              <h3 className="text-lg font-medium text-gray-300">Enter Amount</h3>
              <p className="text-gray-500 mt-1">QR Code will appear here</p>
            </div>
          </div>
        )}

        <div className="mt-8 text-center border-t border-gray-700 pt-6">
          <p className="text-sm text-gray-400">
            Need your own payment link?  
            <a 
              href="/mypaylink" 
              className="text-indigo-400 hover:text-indigo-300 underline ml-1 transition"
            >
              Create Now
            </a>
          </p>
          <p className="text-xs text-gray-600 mt-2">UPI ID: {UpiID}</p>
        </div>
      </motion.div>
    </div>
  );
};

export default UPIPay;